import { useState, useEffect } from 'react';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, onSnapshot, serverTimestamp, where, Timestamp, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Utility function to migrate localStorage data to Firebase
const migrateFromLocalStorage = async (collectionName) => {
  try {
    const storedData = localStorage.getItem(`firebase_${collectionName}`) || '[]';
    const data = JSON.parse(storedData);

    if (data.length > 0) {
      console.log(`Migrating ${data.length} items from localStorage to Firebase for ${collectionName}`);
      
      const collectionRef = collection(db, collectionName);
      const existingDocsSnapshot = await getDocs(collectionRef);

      // Skip migration if Firebase already has data
      if (!existingDocsSnapshot.empty) {
        console.log(`Firebase collection ${collectionName} already has data, skipping migration`);
        return;
      }

      // Add each document to Firebase
      for (const item of data) {
        const { id, ...itemData } = item;
        
        // Convert date strings to Firebase timestamps
        const processedData = {
          ...itemData,
          created_at: item.created_at ? new Date(item.created_at) : new Date(),
          updated_at: item.updated_at ? new Date(item.updated_at) : new Date(),
          published_at: item.published_at ? new Date(item.published_at) : null
        };

        // Use setDoc to preserve the original ID
        await setDoc(doc(db, collectionName, id), processedData);
      }

      console.log(`Migration completed for ${collectionName}`);
    }
  } catch (error) {
    console.error(`Error migrating ${collectionName} from localStorage:`, error);
  }
};

export const useFirestore = (collectionName) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useLocalStorage, setUseLocalStorage] = useState(false);

  // LocalStorage fallback functions
  const getFromLocalStorage = () => {
    try {
      const data = JSON.parse(localStorage.getItem(`firebase_${collectionName}`) || '[]');
      return data.map(item => ({
        ...item,
        created_at: item.created_at ? new Date(item.created_at) : new Date(),
        updated_at: item.updated_at ? new Date(item.updated_at) : new Date(),
        published_at: item.published_at ? new Date(item.published_at) : null
      }));
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  };

  const saveToLocalStorage = (data) => {
    try {
      localStorage.setItem(`firebase_${collectionName}`, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  useEffect(() => {
    let unsubscribe;

    const setupFirestore = async () => {
      // Try to migrate data from localStorage to Firebase
      await migrateFromLocalStorage(collectionName);

      try {
        const q = query(
          collection(db, collectionName),
          orderBy('created_at', 'desc')
        );

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const docs = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              docs.push({
                id: doc.id,
                ...data,
                created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : data.created_at || new Date(),
                updated_at: data.updated_at instanceof Timestamp ? data.updated_at.toDate() : data.updated_at || new Date(),
                published_at: data.published_at instanceof Timestamp ? data.published_at.toDate() : data.published_at || null
              });
            });

            setDocuments(docs);
            setLoading(false);
            setError(null);
            setUseLocalStorage(false);

            // Sync to localStorage as backup
            saveToLocalStorage(docs);
          },
          (error) => {
            console.error('Firestore error:', error);
            console.warn('Falling back to localStorage due to Firebase error');
            
            // Fall back to localStorage
            const localData = getFromLocalStorage();
            setDocuments(localData);
            setUseLocalStorage(true);
            setError(`Firebase Error: ${error.message}. Using local storage.`);
            setLoading(false);

            // Create sample data if localStorage is empty
            if (localData.length === 0) {
              createSampleData(collectionName);
            }
          }
        );
      } catch (error) {
        console.error('Firebase setup error:', error);
        console.warn('Using localStorage due to Firebase setup failure');
        
        // Fall back to localStorage
        const localData = getFromLocalStorage();
        setDocuments(localData);
        setUseLocalStorage(true);
        setError(`Firebase Setup Error: ${error.message}. Using local storage.`);
        setLoading(false);

        // Create sample data if localStorage is empty
        if (localData.length === 0) {
          createSampleData(collectionName);
        }
      }
    };

    setupFirestore();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [collectionName]);

  const createSampleData = (collectionName) => {
    try {
      // Only create sample data if no data exists
      if (documents.length === 0) {
        let sampleData = [];

        // Create collection-specific sample data
        if (collectionName === 'agencies') {
          sampleData = [
            {
              id: '1',
              name: 'TechSEO Agency',
              contact_person: 'John Smith',
              email: 'john@techseo.com',
              phone: '+1-555-0123',
              niche_type: 'normal',
              total_articles: 50,
              total_price: 2500,
              price_per_article: 50,
              remaining_articles: 15,
              created_at: new Date(),
              updated_at: new Date()
            },
            {
              id: '2',
              name: 'Digital Marketing Pro',
              contact_person: 'Sarah Johnson',
              email: 'sarah@digitalmarketing.com',
              phone: '+1-555-0456',
              niche_type: 'special',
              total_articles: 30,
              total_price: 3000,
              price_per_article: 100,
              remaining_articles: 5,
              created_at: new Date(),
              updated_at: new Date()
            }
          ];
        } else if (collectionName === 'domains') {
          sampleData = [
            {
              id: '1',
              name: 'Tech Blog',
              url: 'https://techblog.com',
              domain_authority: 65,
              spam_score: 5.2,
              niche: 'Technology',
              favicon_url: 'https://techblog.com/favicon.ico',
              notes: 'Popular tech blog with high engagement',
              created_at: new Date(),
              updated_at: new Date()
            },
            {
              id: '2',
              name: 'Marketing Hub',
              url: 'https://marketinghub.com',
              domain_authority: 58,
              spam_score: 12.8,
              niche: 'Marketing',
              favicon_url: 'https://marketinghub.com/favicon.ico',
              notes: 'Marketing focused publication',
              created_at: new Date(),
              updated_at: new Date()
            },
            {
              id: '3',
              name: 'Health News Today',
              url: 'https://healthnewstoday.com',
              domain_authority: 72,
              spam_score: 3.1,
              niche: 'Health',
              favicon_url: 'https://healthnewstoday.com/favicon.ico',
              notes: 'High authority health publication',
              created_at: new Date(),
              updated_at: new Date()
            }
          ];
        } else if (collectionName === 'articles') {
          sampleData = [
            {
              id: '1',
              title: 'How to Boost Your SEO Rankings - TechSEO Agency',
              article_url: 'https://techblog.com/boost-seo-rankings',
              agency_id: '1',
              domain_id: '1',
              status: 'published',
              price: 50,
              content: 'Sample article content...',
              keywords: 'normal',
              word_count: 1000,
              published_at: new Date(),
              created_at: new Date(),
              updated_at: new Date()
            },
            {
              id: '2',
              title: 'Advanced Marketing Strategies - Digital Marketing Pro',
              article_url: 'https://marketinghub.com/advanced-marketing-strategies',
              agency_id: '2',
              domain_id: '2',
              status: 'completed',
              price: 100,
              content: 'Sample article content...',
              keywords: 'special',
              word_count: 1200,
              published_at: null,
              created_at: new Date(),
              updated_at: new Date()
            }
          ];
        }

        setDocuments(sampleData);
        saveToLocalStorage(sampleData);
        console.log(`Created sample data for ${collectionName}`);
      }
    } catch (err) {
      console.error('Error creating sample data:', err);
    }
  };

  const addDocument = async (data) => {
    try {
      if (useLocalStorage) {
        // Use localStorage
        const newDoc = {
          ...data,
          id: Date.now().toString(),
          created_at: new Date(),
          updated_at: new Date()
        };
        const updatedDocs = [newDoc, ...documents];
        setDocuments(updatedDocs);
        saveToLocalStorage(updatedDocs);
        return newDoc.id;
      } else {
        // Use Firebase
        const docData = {
          ...data,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        };
        const docRef = await addDoc(collection(db, collectionName), docData);
        return docRef.id;
      }
    } catch (error) {
      console.error('Error adding document:', error);
      // Fallback to localStorage if Firebase fails
      if (!useLocalStorage) {
        console.warn('Falling back to localStorage for add operation');
        setUseLocalStorage(true);
        return await addDocument(data);
      }
      setError(error.message);
      throw error;
    }
  };

  const updateDocument = async (id, data) => {
    try {
      if (useLocalStorage) {
        // Use localStorage
        const updatedDocs = documents.map(doc =>
          doc.id === id ? { ...doc, ...data, updated_at: new Date() } : doc
        );
        setDocuments(updatedDocs);
        saveToLocalStorage(updatedDocs);
      } else {
        // Use Firebase
        const docRef = doc(db, collectionName, id);
        await updateDoc(docRef, {
          ...data,
          updated_at: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating document:', error);
      // Fallback to localStorage if Firebase fails
      if (!useLocalStorage) {
        console.warn('Falling back to localStorage for update operation');
        setUseLocalStorage(true);
        return await updateDocument(id, data);
      }
      setError(error.message);
      throw error;
    }
  };

  const deleteDocument = async (id) => {
    try {
      if (useLocalStorage) {
        // Use localStorage
        const updatedDocs = documents.filter(doc => doc.id !== id);
        setDocuments(updatedDocs);
        saveToLocalStorage(updatedDocs);
      } else {
        // Use Firebase
        const docRef = doc(db, collectionName, id);
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      // Fallback to localStorage if Firebase fails
      if (!useLocalStorage) {
        console.warn('Falling back to localStorage for delete operation');
        setUseLocalStorage(true);
        return await deleteDocument(id);
      }
      setError(error.message);
      throw error;
    }
  };

  const getDocumentsByQuery = async (field, operator, value) => {
    try {
      if (useLocalStorage) {
        // Use localStorage with filtering
        return documents.filter(doc => {
          switch (operator) {
            case '==': return doc[field] === value;
            case '!=': return doc[field] !== value;
            case '>': return doc[field] > value;
            case '>=': return doc[field] >= value;
            case '<': return doc[field] < value;
            case '<=': return doc[field] <= value;
            default: return true;
          }
        });
      } else {
        // Use Firebase
        const q = query(
          collection(db, collectionName),
          where(field, operator, value),
          orderBy('created_at', 'desc')
        );
        const snapshot = await getDocs(q);
        const docs = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          docs.push({
            id: doc.id,
            ...data,
            created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : data.created_at || new Date(),
            updated_at: data.updated_at instanceof Timestamp ? data.updated_at.toDate() : data.updated_at || new Date(),
            published_at: data.published_at instanceof Timestamp ? data.published_at.toDate() : data.published_at || null
          });
        });
        return docs;
      }
    } catch (error) {
      console.error('Error querying documents:', error);
      // Fallback to localStorage if Firebase fails
      if (!useLocalStorage) {
        console.warn('Falling back to localStorage for query operation');
        setUseLocalStorage(true);
        return await getDocumentsByQuery(field, operator, value);
      }
      setError(error.message);
      throw error;
    }
  };

  return {
    documents,
    loading,
    error,
    useLocalStorage,
    addDocument,
    updateDocument,
    deleteDocument,
    getDocumentsByQuery
  };
};